import {create} from 'zustand';
import {persist} from 'zustand/middleware';
import {CartItem, ShopProduct} from './product';

interface CartStore {
  cart: CartItem[];
  addToCart: (product: ShopProduct) => void;
  updateQuantity: (id: number, quantity: number) => void;
  removeItem: (id: number) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getCartCount: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      cart: [],
      
      addToCart: (product: ShopProduct) => {
        const cart = get().cart;
        const existingItemIndex = cart.findIndex(item => item.id === product.id);
        
        if (existingItemIndex !== -1) {
          // Product exists, increase quantity
          const updatedCart = [...cart];
          const currentQuantity = updatedCart[existingItemIndex].quantity;
          const maxQuantity = updatedCart[existingItemIndex].qty?.max || 999;
          
          if (currentQuantity < maxQuantity) {
            updatedCart[existingItemIndex].quantity++;
            set({cart: updatedCart});
          }
        } else {
          // Add new product to cart
          const newItem: CartItem = {
            id: product.id,
            name: product.name,
            quantity: 1,
            image: product.image,
            price: product.price,
            qty: product.qty,
            localCurrencyRate: product.localCurrencyRate,
            localCurrency: product.localCurrency,
            storage: product.storage,
          };
          set({cart: [...cart, newItem]});
        }
      },
      
      updateQuantity: (id: number, quantity: number) => {
        const cart = get().cart;
        const itemIndex = cart.findIndex(item => item.id === id);
        
        if (itemIndex !== -1) {
          if (quantity === 0) {
            // Remove item if quantity is 0
            get().removeItem(id);
          } else {
            const updatedCart = [...cart];
            const maxQuantity = updatedCart[itemIndex].qty?.max || 999;
            updatedCart[itemIndex].quantity = Math.min(quantity, maxQuantity);
            set({cart: updatedCart});
          }
        }
      },
      
      removeItem: (id: number) => {
        const cart = get().cart;
        set({cart: cart.filter(item => item.id !== id)});
      },
      
      clearCart: () => {
        set({cart: []});
      },
      
      getCartTotal: () => {
        const cart = get().cart;
        return cart.reduce((total, item) => {
          const basePrice = parseFloat(item.price[0].price);
          const price = item.localCurrencyRate || basePrice;
          return total + (price * item.quantity);
        }, 0);
      },
      
      getCartCount: () => {
        const cart = get().cart;
        return cart.reduce((count, item) => count + item.quantity, 0);
      },
    }),
    {
      name: 'cart-storage',
    }
  )
);
